<template>
    <div class="addresses-item">
        <v-autocomplete
            ref="autocomplete"
            :label="`${itemLabel}*`"
            v-model="localAddress.selected"
            :items="suggestions"
            v-model:search-input="searchQuery"
            v-model:search="searchQuery"
            no-filter
            item-title="label"
            item-value="id"
            return-object
            variant="outlined"
            bg-color="#fffdfd"
            hide-no-data
            :hint="regAddrToString(localAddress.selected?.intermediate, localAddress.selected?.addressName)"
            :rules="[() => !!localAddress.selected || 'Обязательное поле']"
            :loading="isLoading"
            @change="handleChange"
            @blur="handleChange"
            @update:focused="onFocusChange"
            :menu-props="{ scrollStrategy: 'none', contentClass: 'rounded-lg' }"
        >
            <template v-slot:item="{ props, item }">
                <v-list-item
                    v-bind="props"
                    :subtitle="regAddrToString(item.raw.intermediate, item.raw.addressName)"
                    :title="item.raw.label"
                ></v-list-item>
            </template>

            <template v-slot:prepend-inner>
                <v-icon :icon="mdiMapMarker" :color="prependIconColor" style="opacity: 1"></v-icon>
            </template>

            <template v-slot:append-inner>
                <div
                    v-show="isAutocompleteClearable && searchQuery"
                    class="addresses-item__clear"
                    style="
                        position: absolute;
                        top: 1px;
                        right: 11px;
                        height: 54px;
                        background: #fffdfd;
                        cursor: pointer;
                        z-index: 1;
                    "
                    @click.stop="handleClearSelection(localAddress)"
                >
                    <v-icon :icon="mdiCloseCircle" color="#000000aa"></v-icon>
                </div>
            </template>

            <template v-slot:append>
                <div ref="floorButton">
                    <v-menu
                        v-model="menu"
                        :close-on-content-click="false"
                        location="start"
                        scroll-strategy="none"
                        min-width="265"
                    >
                        <template v-slot:activator="{ props }">
                            <div
                                v-ripple
                                class="addresses-item__floor"
                                :class="{
                                    'addresses-item__floor--chosen': localAddress.floor,
                                    'addresses-item__floor--disabled': !localAddress.selected,
                                }"
                                v-bind="props"
                                style="position: relative"
                            >
                                <template v-if="!localAddress.floor">
                                    <p>Этаж</p>
                                </template>
                                <template v-else>
                                    <v-chip
                                        class="addresses-item__floor-bage"
                                        variant="flat"
                                        size="x-small"
                                        density="comfortable"
                                        color="#fff"
                                        >{{ localAddress.floor }}</v-chip
                                    >
                                    <div class="addresses-item__floor-elevator">
                                        <v-icon
                                            v-if="localAddress.isElevator && !localAddress.isElevatorCargo"
                                            :icon="mdiElevatorPassengerOutline"
                                            color="#fff"
                                            size="small"
                                        ></v-icon>
                                        <v-icon
                                            v-else-if="localAddress.isElevatorCargo"
                                            :icon="mdiElevatorPassenger"
                                            color="#fff"
                                            size="small"
                                        ></v-icon>
                                        <v-icon
                                            v-else
                                            :icon="mdiElevatorPassengerOffOutline"
                                            color="#fff"
                                            size="small"
                                        ></v-icon>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <v-card class="px-4 py-4" min-width="250">
                            <v-text-field
                                v-model="localAddress.floor"
                                label="Этаж"
                                variant="underlined"
                                type="text"
                                placeholder="Введите число"
                                v-mask="'###'"
                                autofocus
                                hide-spin-buttons
                                clearable
                                @input="normalizeFloor"
                            ></v-text-field>
                            <v-switch
                                class="v-switch--inverted"
                                v-model="localAddress.isElevator"
                                label="Лифт"
                                hide-details
                                color="#0eccaa"
                                :disabled="!localAddress.floor"
                            ></v-switch>
                            <v-switch
                                class="v-switch--inverted"
                                v-model="localAddress.isElevatorCargo"
                                label="Грузовой лифт"
                                hide-details
                                color="#0eccaa"
                                :disabled="!localAddress.isElevator"
                            ></v-switch>
                        </v-card>
                    </v-menu>
                </div>

                <v-tooltip v-model="isTooltipActive" :disabled="localAddress.floor > 0 || !localAddress.selected || menu" :activator="tooltipActivator" location="left" text="Укажите этаж">
                </v-tooltip>

                <div
                    v-if="!isAutocompleteClearable && removable && index > 0 && draggable"
                    class="d-inline-flex justify-center align-center"
                    style="
                        position: absolute;
                        top: 1px;
                        right: 69px;
                        height: 54px;
                        background: #fffdfd;
                        cursor: pointer;
                    "
                    @click="handleAddressDeleting"
                >
                    <v-icon :icon="mdiDelete" color="#ff8a90"></v-icon>
                </div>

                <div
                    v-if="index > 0 && draggable"
                    class="d-inline-flex justify-center align-center js-draggable-handle"
                    style="position: absolute; top: 16px; left: 10px; background: #fffdfd; cursor: pointer"
                >
                    <v-icon :icon="mdiDragHorizontalVariant"></v-icon>
                </div>
            </template>
        </v-autocomplete>
    </div>
</template>

<script setup>
import {
    mdiMapMarker,
    mdiCloseCircle,
    mdiElevatorPassengerOutline,
    mdiElevatorPassenger,
    mdiElevatorPassengerOffOutline,
    mdiDelete,
    mdiDragHorizontalVariant,
} from '@mdi/js';
</script>

<script>
import axios from 'axios';
import debounce from 'lodash.debounce';
import { mask } from 'vue-the-mask';

const MAX_SUGGESTIONS = 16;
const TOKEN = '27b21cb1-37db-439a-8c9a-c27cd5f06f29';

export default {
    name: 'AddressItem',

    emits: ['address-change', 'addresses-error', 'address-deleting', 'gis-api-call'],

    directives: { mask },

    props: {
        index: {
            type: Number,
            required: true,
        },
        isLastItem: {
            type: Boolean,
            required: true,
        },
        ipgeo: {
            type: Object,
            required: true,
        },
        address: {
            type: Object,
            rquired: true,
        },
        removable: {
            type: Boolean,
            required: true,
        },
        draggable: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        return {
            searchQuery: '',
            suggestions: [],
            menu: false,
            isAutocompleteClearable: false,
            isTooltipActive: false,
            tooltipActivator: null,
            isLoading: false,
            localAddress: JSON.parse(JSON.stringify(this.address)),
        };
    },

    computed: {
        itemLabel() {
            return this.index === 0 ? 'Откуда' : this.isLastItem ? 'Куда' : 'Заехать';
        },

        prependIconColor() {
            return this.index > 0 ? '#ff8a90' : '#4bace3';
        },
    },

    mounted() {
        this.tooltipActivator = this.$refs.floorButton;
    },

    created() {
        this.debouncedUpdate = debounce(function () {
            this.fetchSuggestions(this.searchQuery);
        }, 700);
    },

    watch: {
        ['localAddress.selected'](oldValue, newValue) {
            if (oldValue === newValue) {
                return;
            }
            this.toggleTooltip();
        },

        ['localAddress.floor'](newValue) {
            if (newValue < 2) {
                this.localAddress.isElevator = false;
                this.localAddress.isElevatorCargo = false;
            }
            if (newValue > 160) {
                this.localAddress.floor = 160;
            }
            if (newValue < 0) {
                this.localAddress.floor = 1;
            }
        },

        ['localAddress.isElevator'](newValue) {
            if (newValue === false) {
                this.localAddress.isElevatorCargo = false;
            }
        },

        searchQuery(newValue) {
            if (newValue) {
                this.isLoading = true;
                this.debouncedUpdate();
            } else {
                this.suggestions = [];
            }
        },
        localAddress: {
            handler: function (newAddress) {
                if (!newAddress.selected) return;
                this.$emit('address-change', { index: this.index, address: newAddress });
            },
            deep: true,
        },
    },

    methods: {
        async fetchSuggestions(search) {
            if (!search) {
                return;
            }

            this.$emit('gis-api-call');

            if (this.ipgeo.longitude && this.ipgeo.latitude)
                search += `&location=${this.ipgeo.longitude},${this.ipgeo.latitude}`;

            await axios
                .get(
                    `https://catalog.api.2gis.com/3.0/suggests?
&q=${search}
&page_size=${MAX_SUGGESTIONS}
&fields=items.name_ex,
items.point,
items.rubrics,
items.org,
items.adm_div,
items.routes,
items.type,
items.subtype,
items.address,
items.search_attributes.personal_priority,
items.search_attributes.dgis_source_type,
items.search_attributes.dgis_found_by_address,
items.segment_id,
items.region_id,
items.locale,
items.group,
items.context,
search_attributes,
items.flags,
items.has_exchange,
items.ads.options
&type=adm_div.region,
adm_div.district_area,
adm_div.city,
adm_div.settlement,
adm_div.district,
adm_div.living_area,
adm_div.division,
adm_div.place,
street,branch,
building,
road,
attraction,
crossroad,
route,
route_type,
station,
station.metro,
user_queries,
attribute,
rubric,
meta_rubric,
org,special,
coordinates,
kilometer_road_sign
&locale=ru_RU
&key=${TOKEN}`
                )
                .then((response) => {
                    const suggestions = response.data.result.items;

                    if (suggestions) {
                        this.suggestions = this.normalizeSuggestions(suggestions);
                    } else {
                        this.suggestions = [];
                    }
                })
                .catch((error) => {
                    console.error('Error fetching suggestions:', error);
                })
                .finally(() => (this.isLoading = false));
        },

        normalizeSuggestions(suggestions) {
            const suggestionsObjects = suggestions.map(function (item) {
                let id = item.id;
                // set region
                let region = null;
                if (item.adm_div) {
                    for (const adm_div of item.adm_div) {
                        if (adm_div.type == 'region') {
                            region = adm_div.name;
                            break;
                        }
                    }
                }

                // set label
                let label = null;
                if (item.full_name || item.address_name) label = item.full_name || item.address_name;

                // set address name
                let addressName = null;
                if (item.address_name) {
                    addressName = item.address_name;
                }

                // set geolocation point
                let point = null;
                if (item.point) {
                    point = item.point;
                }

                // set all intermediate names, like district etc
                let intermediate = '';
                if (item.adm_div) {
                    intermediate = item.adm_div.map((x) => x.name).join(', ');
                }

                return {
                    id,
                    label,
                    intermediate,
                    addressName,
                    region,
                    point,
                };
            });

            return suggestionsObjects.filter((x) => x.label || x.addressName);
        },

        regAddrToString(intermediate, addr, label) {
            const joinedArr = [intermediate, addr, label]
                .filter(Boolean)
                .map((x) => x.split(', '))
                .join()
                .split(',');
            return [...new Set(joinedArr)].join(', ');
        },

        normalizeFloor() {
            if (this.localAddress.floor.length === 1 && this.localAddress.floor.startsWith('0')) {
                this.localAddress.floor = '';
            }
        },

        onFocusChange(e) {
            if (e) {
                this.isAutocompleteClearable = true;
            } else {
                this.isAutocompleteClearable = false;
            }
        },

        handleClearSelection() {
            this.searchQuery = '';
            this.localAddress.selected = null;
            this.$emit('address-change', { index: this.index, address: { ...this.localAddress, floor: null } });
        },

        handleChange(value) {
            if (!value) {
                this.$emit('addresses-error', 'Обязательное поле');
            }
        },

        handleAddressDeleting() {
            this.$emit('address-deleting', this.index);
        },

        toggleTooltip() {
            if (this.localAddress.floor || !this.localAddress.selected) {
                return;
            }

            if (this.localAddress.selected && !this.localAddress.floor) {
                this.isTooltipActive = true;
                setTimeout(() => {
                    this.isTooltipActive = false;
                }, 2000);
            }
        },
    },
};
</script>

<style lang="scss">
.addresses-item {
    position: relative;
    width: 100%;

    &__floor {
        position: relative;
        display: inline-flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        width: 42px;
        height: 56px !important;
        padding: 4px;
        border-radius: 8px;
        background-color: rgba(73, 73, 73, 0.12);
        cursor: pointer;
        user-select: none;

        &--disabled {
            opacity: 0.6;
            pointer-events: none;
            cursor: default;
        }

        &--chosen {
            background-color: #0eccaa;
        }

        p {
            font-size: 10px;
            line-height: normal;
            text-transform: uppercase;
            text-align: center;
        }
    }

    &__clear {
        display: inline-flex;
        justify-content: center;
        align-items: center;
    }
}
</style>
